import React, { useState } from 'react';
import { Card, Typography, Space, Button, message } from 'antd';
import ImageUpload from '../shared/ImageUpload';
import { uploadService } from '../../services/uploadService';

const { Title, Text } = Typography;

const ImageUploadTest: React.FC = () => {
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleImageChange = (urls: string[]) => {
    setImageUrls(urls);
    console.log('Image URLs updated:', urls);
  };

  const testSingleUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      setUploading(true);
      try {
        const result = await uploadService.uploadImage(file);
        if (result.success && result.data) {
          setImageUrls(prev => [...prev, result.data!.url]);
          message.success('Image uploaded successfully!');
        } else {
          message.error(result.error || 'Upload failed');
        }
      } catch (error) {
        message.error('Upload failed');
      } finally {
        setUploading(false);
      }
    };

    input.click();
  };

  const clearAllImages = () => {
    setImageUrls([]);
    message.info('All images cleared');
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Card>
          <Title level={2}>Image Upload Test</Title>
          <Text type="secondary">
            Test the Cloudinary integration with our ImageUpload component
          </Text>
        </Card>

        <Card title="Image Upload Component">
          <ImageUpload
            value={imageUrls}
            onChange={handleImageChange}
            maxCount={5}
            folder="test"
          />
        </Card>

        <Card title="Manual Upload Test">
          <Space>
            <Button 
              onClick={testSingleUpload}
              loading={uploading}
              type="primary"
            >
              Test Single Upload
            </Button>
            <Button 
              onClick={clearAllImages}
              disabled={imageUrls.length === 0}
            >
              Clear All Images
            </Button>
          </Space>
        </Card>

        <Card title="Current Image URLs">
          {imageUrls.length > 0 ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              {imageUrls.map((url, index) => (
                <div key={index}>
                  <Text strong>Image {index + 1}:</Text>
                  <br />
                  <Text code copyable style={{ fontSize: '12px' }}>
                    {url}
                  </Text>
                </div>
              ))}
            </Space>
          ) : (
            <Text type="secondary">No images uploaded yet</Text>
          )}
        </Card>

        <Card title="Transformation Examples">
          {imageUrls.length > 0 && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Original:</Text>
                <br />
                <img 
                  src={imageUrls[0]} 
                  alt="Original" 
                  style={{ maxWidth: '200px', height: 'auto', border: '1px solid #d9d9d9' }}
                />
              </div>
              
              <div>
                <Text strong>Thumbnail (150x150, cropped):</Text>
                <br />
                <img 
                  src={uploadService.getThumbnailUrl(imageUrls[0], 150)} 
                  alt="Thumbnail" 
                  style={{ border: '1px solid #d9d9d9' }}
                />
              </div>
              
              <div>
                <Text strong>Responsive (300px wide):</Text>
                <br />
                <img 
                  src={uploadService.getResponsiveUrl(imageUrls[0], 300)} 
                  alt="Responsive" 
                  style={{ maxWidth: '300px', height: 'auto', border: '1px solid #d9d9d9' }}
                />
              </div>
            </Space>
          )}
        </Card>
      </Space>
    </div>
  );
};

export default ImageUploadTest;